import React from "react";
import {
  useGetApplicantDocumentsQuery,
  useGetDocumentTypesQuery,
  usePostUpsertApplicantDocumentMutation,
  usePostUpsertApplicationMutation,
} from "@/store/service";
import { useForm } from "react-hook-form";
import { ApplicationStep } from "@/enum/ApplicationStep";
import Applicants from "@/model/interface/form/Applicants";
import { useApplicantChecks } from "@/components/provider/ApplicantChecksProvider";
import NewApplicantID from "@/components/subcomponents/application-form/NewApplicantID";
import Skeleton from "react-loading-skeleton";
import { isDebugForms, getDebugNextStepUrl } from "@/util/debug";
import { useNavigate } from "react-router-dom";
import { applicantsIdSchema } from "@/model/schemas";
import { zodFormResolver } from "@/util/form";

const ApplicantIDWrapper = () => {
  const navigate = useNavigate();
  const { applicant, applicationId, applicantType } = useApplicantChecks();

  const { data: documentTypes } = useGetDocumentTypesQuery();
  const { data: applicantDocuments } = useGetApplicantDocumentsQuery(applicant?.id!, {
    skip: !applicant?.id,
  });

  const [postApplicantDocument] = usePostUpsertApplicantDocumentMutation();
  const [postApplication] = usePostUpsertApplicationMutation();

  const { handleSubmit } = useForm<Applicants>({
    resolver: zodFormResolver<Applicants>(applicantsIdSchema),
    mode: "onTouched",
    defaultValues: {},
  });

  const totalPoints = applicantDocuments?.reduce((acc, cur) => acc + cur.documentType?.pointsValue, 0) ?? 0;

  const onDocumentSubmit = async (
    documentType: string,
    documentFields: Record<string, object>,
    documentFiles: File[],
  ) => {
    if (!applicant) {
      return;
    }

    await postApplicantDocument({
      applicantId: applicant.id,
      documentType,
      data: {
        fieldValues: { ...documentFields, type: documentType },
      },
    }).unwrap();
  };

  const onSubmit = async () => {
    if (!applicant) {
      return;
    }
    const step = applicantType === "main-applicant" ? ApplicationStep.ApplicantID : ApplicationStep.CoApplicantID;
    postApplication({ id: applicationId, step });
  };

  if (!documentTypes) {
    return <Skeleton className="mt-2" count={5} height="3em" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-semibold text-gray-900">Applicant 100 Points ID</h3>
        <div className="mt-3 space-y-2 text-sm text-gray-600">
          <p>
            You must provide a total of 100 points of Australian or state-issued documents to prove your identity
            online.
          </p>
          <p>
            Different types of identity documents are worth different points. You can use different combinations of
            documents to make up your 100 points, but you must include at least 1 primary document as part of your 100
            points.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex-1 text-sm text-gray-700">
          Current ID points: <strong className="text-gray-900">{totalPoints}</strong> / 100
        </div>
        <div className="h-2 flex-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-[#0FD131] transition-all"
            style={{ width: `${Math.min(totalPoints, 100)}%` }}
          />
        </div>
      </div>

      <NewApplicantID documentTypes={documentTypes} onDocumentSubmit={onDocumentSubmit} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isDebugForms()) {
            navigate(getDebugNextStepUrl(applicationId, ApplicationStep.ApplicantID, applicantType));
          } else {
            handleSubmit(onSubmit)(e);
          }
        }}
      >
        <div className="flex justify-center pt-2">
          <button
            type="submit"
            className="inline-flex items-center rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all cursor-pointer"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
};
export default ApplicantIDWrapper;
